import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';

import Card from '../../components/ui/Card';
import Input from '../../components/forms/Input';
import Select from '../../components/forms/Select';
import StartImpersonationModal from '../../components/impersonation/StartImpersonationModal';
import { useAdminOrgs } from '../../hooks/api/useAdminOrgs';
import {
  AdminOrgIssue,
  AdminOrgListItem,
  TrialState,
} from '../../services/api/adminOrgs';
import { useAuthStore } from '../../stores/authStore';
import { formatDate, formatRelative } from '../../utils/date';

// -- Static option lists for filter dropdowns ---------------------------------

// Sellable tiers come first; deprecated tiers are kept here so super admins
// can still filter/identify legacy DB rows on plans that are no longer sold.
const tierOptions = [
  { value: '', label: 'All tiers' },
  { value: 'seedling', label: 'Seedling (free)' },
  { value: 'sprout', label: 'Sprout' },
  { value: 'sapling', label: 'Sapling (legacy)' },
  { value: 'canopy', label: 'Canopy (legacy)' },
  { value: 'forest', label: 'Forest (legacy)' },
];

const trialStateOptions: Array<{ value: '' | TrialState; label: string }> = [
  { value: '', label: 'Any trial state' },
  { value: 'never_used', label: 'Never trialled' },
  { value: 'active', label: 'Trial active' },
  { value: 'expired', label: 'Trial expired' },
];

const issuesFilterOptions = [
  { value: '', label: 'All orgs' },
  { value: 'true', label: 'Only with issues' },
  { value: 'false', label: 'Only healthy' },
];

// -- Visual style maps --------------------------------------------------------

const tierBadgeClass: Record<string, string> = {
  seedling: 'bg-gray-100 text-gray-700',
  sapling: 'bg-green-100 text-green-700',
  sprout: 'bg-emerald-100 text-emerald-700',
  canopy: 'bg-purple-100 text-purple-700',
  forest: 'bg-amber-100 text-amber-700',
};

const trialStateBadgeClass: Record<TrialState, string> = {
  none: 'bg-gray-100 text-gray-600',
  never_used: 'bg-gray-100 text-gray-600',
  active: 'bg-blue-100 text-blue-700',
  expired: 'bg-rose-100 text-rose-700',
};

const issueSeverityClass: Record<AdminOrgIssue['severity'], string> = {
  warning: 'bg-amber-100 text-amber-800 border-amber-200',
  critical: 'bg-rose-100 text-rose-800 border-rose-200',
};

// Keep filter labels stable when state values change.
const trialStateLabel: Record<TrialState, string> = {
  none: 'None',
  never_used: 'Never trialled',
  active: 'Trial',
  expired: 'Expired',
};

// -- Helpers ------------------------------------------------------------------

function describeTrial(org: AdminOrgListItem): string {
  if (org.trial_state === 'active' && org.trial_days_remaining !== null) {
    const d = org.trial_days_remaining;
    return `${d} day${d === 1 ? '' : 's'} left${org.trial_tier ? ` on ${org.trial_tier}` : ''}`;
  }
  if (org.trial_state === 'expired') {
    return org.trial_tier ? `Was trialling ${org.trial_tier}` : 'Trial used';
  }
  return 'No trial taken';
}

// -- Page ---------------------------------------------------------------------

const OrgManagement: React.FC = () => {
  const [search, setSearch] = useState('');
  const [tier, setTier] = useState('');
  const [trialState, setTrialState] = useState<'' | TrialState>('');
  const [hasIssuesFilter, setHasIssuesFilter] = useState<'' | 'true' | 'false'>('');
  const [impersonationTarget, setImpersonationTarget] = useState<{
    id: string;
    name: string;
    code: string;
  } | null>(null);
  const currentUser = useAuthStore(s => s.user);

  const queryParams = useMemo(
    () => ({
      search: search || undefined,
      tier: tier || undefined,
      trial_state: (trialState || undefined) as TrialState | undefined,
      has_issues:
        hasIssuesFilter === 'true'
          ? true
          : hasIssuesFilter === 'false'
            ? false
            : undefined,
    }),
    [search, tier, trialState, hasIssuesFilter],
  );

  const { data: orgs, isLoading, isError, error } = useAdminOrgs(queryParams);

  // Surfaced summary counts above the table — gives operators an at-a-glance
  // health read before they scan rows.
  const summary = useMemo(() => {
    if (!orgs) return null;
    const total = orgs.length;
    const activeTrials = orgs.filter(o => o.trial_state === 'active').length;
    const expiringSoon = orgs.filter(o =>
      o.issues.some(i => i.code === 'trial_expiring_soon'),
    ).length;
    const silentlyReverted = orgs.filter(o =>
      o.issues.some(i => i.code === 'trial_expired_silently'),
    ).length;
    const orphaned = orgs.filter(o =>
      o.issues.some(i => i.code === 'no_users'),
    ).length;
    return { total, activeTrials, expiringSoon, silentlyReverted, orphaned };
  }, [orgs]);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-display font-bold text-gray-900 dark:text-gray-100">
              Organization Management
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Trial state, tier health, and operational issues across all
              tenants.
            </p>
          </div>
        </div>

        {/* Summary tiles */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <SummaryTile label="Total orgs" value={summary.total} />
            <SummaryTile label="Active trials" value={summary.activeTrials} tone="info" />
            <SummaryTile
              label="Expiring soon"
              value={summary.expiringSoon}
              tone={summary.expiringSoon > 0 ? 'warning' : 'neutral'}
            />
            <SummaryTile
              label="Silently reverted"
              value={summary.silentlyReverted}
              tone={summary.silentlyReverted > 0 ? 'critical' : 'neutral'}
            />
            <SummaryTile
              label="Orphaned"
              value={summary.orphaned}
              tone={summary.orphaned > 0 ? 'critical' : 'neutral'}
            />
          </div>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search by name or code…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                icon={<MagnifyingGlassIcon className="w-5 h-5 text-gray-400" />}
              />
            </div>
            <div className="w-full md:w-48">
              <Select
                value={tier}
                onChange={e => setTier(e.target.value)}
                options={tierOptions}
              />
            </div>
            <div className="w-full md:w-52">
              <Select
                value={trialState}
                onChange={e => setTrialState(e.target.value as '' | TrialState)}
                options={trialStateOptions}
              />
            </div>
            <div className="w-full md:w-48">
              <Select
                value={hasIssuesFilter}
                onChange={e => setHasIssuesFilter(e.target.value as '' | 'true' | 'false')}
                options={issuesFilterOptions}
              />
            </div>
          </div>
        </Card>

        {/* Table */}
        <Card className="overflow-hidden !p-0">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">Loading organizations…</div>
          ) : isError ? (
            <div className="p-8 text-center text-rose-600">
              Failed to load organizations
              {error instanceof Error ? `: ${error.message}` : '.'}
            </div>
          ) : !orgs || orgs.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No organizations match these filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="admin-orgs-table">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                    <Th>Organization</Th>
                    <Th>Tier</Th>
                    <Th>Trial</Th>
                    <Th>Users</Th>
                    <Th>Last activity</Th>
                    <Th>Issues</Th>
                    <Th>Actions</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {orgs.map(org => {
                    // Hide "View as" for the user's home org and the platform
                    // org -- impersonating yourself is a no-op, and the
                    // PLATFORM org has no tenant data worth viewing.
                    const isHomeOrg = currentUser?.organization_id === org.id;
                    const isPlatformOrg = org.code === 'PLATFORM';
                    return (
                      <OrgRow
                        key={org.id}
                        org={org}
                        canImpersonate={!isHomeOrg && !isPlatformOrg}
                        onViewAs={() =>
                          setImpersonationTarget({
                            id: org.id,
                            name: org.name,
                            code: org.code,
                          })
                        }
                      />
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </motion.div>

      {impersonationTarget !== null && (
        <StartImpersonationModal
          isOpen
          onClose={() => setImpersonationTarget(null)}
          org={impersonationTarget}
        />
      )}
    </div>
  );
};

// -- Subcomponents ------------------------------------------------------------

interface SummaryTileProps {
  label: string;
  value: number;
  tone?: 'neutral' | 'info' | 'warning' | 'critical';
}

const summaryToneClass: Record<NonNullable<SummaryTileProps['tone']>, string> = {
  neutral: 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700',
  info: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
  warning: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
  critical: 'bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-rose-800',
};

const SummaryTile: React.FC<SummaryTileProps> = ({ label, value, tone = 'neutral' }) => (
  <div className={`rounded-xl border p-4 ${summaryToneClass[tone]}`}>
    <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</div>
    <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mt-1">
      {label}
    </div>
  </div>
);

const Th: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
    {children}
  </th>
);

interface OrgRowProps {
  org: AdminOrgListItem;
  canImpersonate: boolean;
  onViewAs: () => void;
}

const OrgRow: React.FC<OrgRowProps> = ({ org, canImpersonate, onViewAs }) => {
  const tieredEffective =
    org.effective_tier !== org.subscription_tier;

  return (
    <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
      {/* Name + code */}
      <td className="px-6 py-4 whitespace-nowrap">
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {org.name}
          {org.is_demo && (
            <span className="ml-2 text-[10px] uppercase tracking-wider text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded">
              Demo
            </span>
          )}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">
          {org.code}
        </p>
      </td>

      {/* Tier — show subscription + effective if differing (i.e. trial active) */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span
            className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full capitalize ${
              tierBadgeClass[org.subscription_tier] ?? 'bg-gray-100 text-gray-700'
            }`}
          >
            {org.subscription_tier}
          </span>
          {tieredEffective && (
            <span
              className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full capitalize ${
                tierBadgeClass[org.effective_tier] ?? 'bg-gray-100 text-gray-700'
              }`}
              title={`Effective tier from active trial: ${org.effective_tier}`}
            >
              ↑ {org.effective_tier}
            </span>
          )}
        </div>
      </td>

      {/* Trial state */}
      <td className="px-6 py-4 whitespace-nowrap">
        <span
          className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
            trialStateBadgeClass[org.trial_state]
          }`}
        >
          {trialStateLabel[org.trial_state]}
        </span>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {describeTrial(org)}
        </p>
        {org.trial_ends_at && (
          <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
            ends {formatDate(org.trial_ends_at)}
          </p>
        )}
      </td>

      {/* Users */}
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
        {org.user_count}
      </td>

      {/* Last activity (audit log) */}
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
        {org.last_activity_at ? formatRelative(org.last_activity_at) : '—'}
      </td>

      {/* Issues */}
      <td className="px-6 py-4">
        {org.issues.length === 0 ? (
          <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {org.issues.map(issue => (
              <span
                key={issue.code}
                title={issue.message}
                className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full border ${
                  issueSeverityClass[issue.severity]
                }`}
              >
                <ExclamationTriangleIcon className="w-3 h-3" aria-hidden="true" />
                {issue.code.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        )}
      </td>

      {/* Actions */}
      <td className="px-6 py-4 whitespace-nowrap">
        {canImpersonate ? (
          <button
            type="button"
            onClick={onViewAs}
            data-testid={`view-as-${org.code}`}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            title={`View ${org.name} as super-admin (read-only)`}
          >
            <EyeIcon className="w-3.5 h-3.5" aria-hidden="true" />
            View as
          </button>
        ) : (
          <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
        )}
      </td>
    </tr>
  );
};

export default OrgManagement;
